import { useParams, Link } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, Volume2, VolumeX, Maximize, ArrowLeft, Download, Film } from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import ShareButtons from '@/components/ShareButtons';
import { VIDEO_LIBRARY } from '@/data/videoData';
import { videoPosterSrc } from '@/components/SocialImage';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const VideoPage = () => {
  const { videoId } = useParams();
  const { adminStatus } = useAuth();
  const [dbVideo, setDbVideo] = useState(null);
  const [allVideos, setAllVideos] = useState(VIDEO_LIBRARY);
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [loading, setLoading] = useState(true);

  const canDownload = adminStatus && ['owner', 'admin', 'manager', 'editor'].includes(adminStatus.role);

  useEffect(() => {
    // Fetch from public API
    fetch(`${API}/api/videos/${videoId}`)
      .then(r => { if (!r.ok) throw new Error('Not found'); return r.json(); })
      .then(data => {
        const v = data.video;
        setDbVideo({
          ...v,
          id: v.video_id,
          icon: Film,
          src: v.src?.startsWith('/') ? `${API}${v.src}` : v.src,
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // Also fetch all published videos for related section
    fetch(`${API}/api/videos`)
      .then(r => r.json())
      .then(data => {
        if (data.videos?.length > 0) {
          setAllVideos(data.videos.map(v => ({
            ...v, id: v.video_id, icon: Film,
            src: v.src?.startsWith('/') ? `${API}${v.src}` : v.src,
          })));
        }
      })
      .catch(() => {});
  }, [videoId]);

  // Use DB video if available, fall back to hardcoded
  const video = dbVideo || VIDEO_LIBRARY.find(v => v.id === videoId);

  if (!loading && !video) {
    return (
      <PageLayout>
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <h1 className="text-3xl font-bold text-taxera-charcoal mb-4">Video not found</h1>
          <Link to="/resources/videos" className="text-taxera-teal hover:underline">
            Back to Video Library
          </Link>
        </div>
      </PageLayout>
    );
  }

  const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
  const ogShareUrl = `${API}/api/og/video/${videoId}`;
  const Icon = video.icon;

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (playing) { videoRef.current.pause(); } else { videoRef.current.play(); }
    setPlaying(!playing);
  };

  const related = allVideos
    .filter(v => v.id !== videoId && v.category === video.category)
    .slice(0, 3);

  return (
    <PageLayout>
      <div data-testid="video-page" className="max-w-5xl mx-auto px-6 py-12">
        {/* Breadcrumb */}
        <Link
          to="/resources/videos"
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-taxera-teal transition-colors mb-8"
          data-testid="video-back-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Video Library
        </Link>

        {/* Video Player */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative rounded-2xl overflow-hidden bg-slate-900 aspect-video mb-8 group"
        >
          <video
            ref={videoRef}
            src={video.src}
            poster={videoPosterSrc(video.src) || undefined}
            muted={muted}
            className="w-full h-full object-cover"
            onEnded={() => setPlaying(false)}
            data-testid="video-player"
          />
          {/* Controls overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="absolute bottom-0 left-0 right-0 p-5 flex items-center gap-4">
              <button onClick={togglePlay} className="text-white hover:text-taxera-teal transition-colors" data-testid="video-play-btn">
                {playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
              </button>
              <button onClick={() => setMuted(!muted)} className="text-white hover:text-taxera-teal transition-colors" data-testid="video-mute-btn">
                {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
              <button
                onClick={() => videoRef.current?.requestFullscreen()}
                className="text-white hover:text-taxera-teal transition-colors ml-auto"
                data-testid="video-fullscreen-btn"
              >
                <Maximize className="w-5 h-5" />
              </button>
            </div>
          </div>
          {/* Big play button when paused */}
          {!playing && (
            <button
              onClick={togglePlay}
              className="absolute inset-0 flex items-center justify-center"
              data-testid="video-big-play"
            >
              <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors">
                <Play className="w-8 h-8 text-white ml-1" />
              </div>
            </button>
          )}
        </motion.div>

        {/* Video Info */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-3">
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-white"
              style={{ backgroundColor: video.color }}
            >
              <Icon className="w-3.5 h-3.5" />
              {video.category}
            </span>
            <span className="text-sm text-slate-400">{video.duration}</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-taxera-charcoal mb-3" data-testid="video-title">
            {video.title}
          </h1>
          {video.tagline && (
            <p className="text-lg font-semibold text-taxera-teal italic mb-4" data-testid="video-tagline">
              "{video.tagline}"
            </p>
          )}
          <p className="text-base text-slate-600 leading-relaxed max-w-2xl mb-6" data-testid="video-description">
            {video.description}
          </p>

          {/* Share + Download */}
          <div className="pt-4 border-t border-slate-200 flex items-center justify-between flex-wrap gap-4" data-testid="video-share-section">
            <ShareButtons
              url={ogShareUrl}
              title={video.title}
              summary={video.description}
            />
            {canDownload && video.src && (
              <a
                href={video.src}
                download={`taxera-${video.id}.mp4`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-taxera-charcoal text-white rounded-lg text-sm font-semibold hover:bg-taxera-charcoal/90 transition-all"
                data-testid="video-download-btn"
              >
                <Download className="w-4 h-4" />
                Download MP4
              </a>
            )}
          </div>
        </motion.div>

        {/* Related Videos */}
        {related.length > 0 && (
          <div>
            <h2 className="font-heading text-xl font-bold text-taxera-charcoal mb-6">
              More {video.category} Videos
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {related.map((rv) => {
                const RIcon = rv.icon;
                return (
                  <Link
                    key={rv.id}
                    to={`/resources/videos/${rv.id}`}
                    className="group block rounded-xl overflow-hidden border border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all"
                    data-testid={`related-video-${rv.id}`}
                  >
                    <div className="relative aspect-video bg-slate-900 flex items-center justify-center">
                      <video src={rv.src} poster={videoPosterSrc(rv.src, true) || undefined} preload="none" muted className="w-full h-full object-cover opacity-60" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/30 transition-colors">
                          <Play className="w-4 h-4 text-white ml-0.5" />
                        </div>
                      </div>
                      <span className="absolute bottom-2 right-2 text-xs text-white/80 bg-black/50 px-2 py-0.5 rounded">{rv.duration}</span>
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <RIcon className="w-3.5 h-3.5" style={{ color: rv.color }} />
                        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: rv.color }}>{rv.category}</span>
                      </div>
                      <h3 className="font-semibold text-sm text-taxera-charcoal group-hover:text-taxera-teal transition-colors">{rv.title}</h3>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default VideoPage;
